import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { env } from '@amfam/environment';
import {
  selectCustomerSegmentCd,
  selectLoaded
} from '@amfam/policy/data-access/lib/+state/summaries/summaries.selectors';
import { isPhoenixOrHorisonSegment } from '@amfam/profile/user-account-preferences/data-access/lib/data-access/models/user-account-preferences.model';
import type { ApiStatus } from '@amfam/shared/models/lib/api/api-status.model';
import { selectIsCustomer, selectWaid } from '@amfam/shared/user/lib/+state/user.selectors';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';

import {
  AddEditPaymentMethodPayload,
  AddEditPaymentMethodResponse,
  DeletePaymentMethodResponse,
  GetPaymentMethodsResponse,
  UpdateModeOfAuthorizationPayload,
  UpdateModeOfAuthorizationResponse
} from '../models/payment-method.model';

@Injectable({ providedIn: 'root' })
export class PaymentMethodService {
  private http = inject(HttpClient);
  private store = inject(Store);
  private appService = inject(ApplicationService);
  private readonly waid = toSignal(this.store.select(selectWaid));

  /** payment-accounts endpoint on the Payment Experience API (/afi routes). */
  get paymentApi(): string {
    return env.paymentExperienceApi + 'afi/payment-accounts';
  }

  getPaymentMethods(): Observable<GetPaymentMethodsResponse> {
    return combineLatest([
      this.store.select(selectCustomerSegmentCd),
      this.store.select(selectIsCustomer),
      this.store.select(selectLoaded)
    ]).pipe(
      // policiesLoaded ensures the filter unblocks for customers with no segment code
      // (e.g. no associated policies) once the policy summary state has fully loaded.
      filter(([segment, isCustomer, policiesLoaded]) => !!segment || !isCustomer || policiesLoaded),
      take(1),
      switchMap(([segment]) => {
        if (isPhoenixOrHorisonSegment(segment)) {
          return of({ paymentAccounts: [] } as unknown as GetPaymentMethodsResponse);
        }
        const params = this.addWaidIfMMA();
        return this.http.get<GetPaymentMethodsResponse>(this.paymentApi, { params }).pipe(
          catchError((err: { status: ApiStatus; error?: { status: ApiStatus } }) => {
            if (
              err?.status?.code?.toString() === '404' ||
              err?.error?.status?.code?.toString() === '404'
            ) {
              return of({
                paymentAccounts: [],
                status: err.status
              });
            }
            throw err;
          })
        );
      })
    );
  }

  addPaymentMethod(payload: AddEditPaymentMethodPayload): Observable<AddEditPaymentMethodResponse> {
    const params = this.addWaidIfMMA();
    const endpoint = this.paymentApi;
    return this.http.post<AddEditPaymentMethodResponse>(endpoint, payload, { params });
  }

  editPaymentMethod(
    payload: AddEditPaymentMethodPayload,
    paymentAccountId?: string
  ): Observable<AddEditPaymentMethodResponse> {
    const endpoint = this.paymentApi + '/' + paymentAccountId;
    const params = this.addWaidIfMMA();
    return this.http.put<AddEditPaymentMethodResponse>(endpoint, payload, { params });
  }

  updateModeOfAuthorization(
    payload: UpdateModeOfAuthorizationPayload
  ): Observable<UpdateModeOfAuthorizationResponse> {
    const endpoint = this.paymentApi + '/' + payload.paymentAccountId;
    const params = this.addWaidIfMMA();
    return this.http.put<UpdateModeOfAuthorizationResponse>(endpoint, payload.requestBody, {
      params
    });
  }

  deletePaymentMethod(paymentAccountId: string): Observable<DeletePaymentMethodResponse> {
    const uri = this.paymentApi + '/' + paymentAccountId;
    const params = this.addWaidIfMMA();
    return this.http.delete<DeletePaymentMethodResponse>(uri, { params });
  }

  private addWaidIfMMA(params: HttpParams = new HttpParams()): HttpParams {
    if (this.appService.isApp(Applications.MYACCOUNT_ADMIN)) {
      const waid = this.waid();
      if (waid) return params.set('waid', waid);
    }
    return params;
  }
}
